import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Mail, Clock, HelpCircle, Settings2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_TERMS_OPTIONS = [
  { value: 'NET_30', label: 'Net 30' },
  { value: 'NET_45', label: 'Net 45' },
  { value: 'NET_60', label: 'Net 60' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'CUSTOM', label: 'Custom' },
];

export default function Customers() {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    externalId: '', 
    poNumberPrefix: '', 
    jobCode: '',
    paymentTerms: 'NET_30',
    paymentTermsDays: '',
    billingAddress: '',
    billingEmail: '',
    config: {
      useDetailedTM: false,
      requireActivity: false,
      trackEquipment: false,
      trackMaterials: false,
      trackPOBalance: false,
      useCustomRates: false
    }
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({ 
        name: customer.name, 
        externalId: customer.externalId || '',
        poNumberPrefix: customer.poNumberPrefix || '',
        jobCode: customer.jobCode || '',
        paymentTerms: customer.paymentTerms || 'NET_30',
        paymentTermsDays: customer.paymentTermsDays || '',
        billingAddress: customer.billingAddress || '',
        billingEmail: customer.billingEmail || '',
        config: customer.config || {
          useDetailedTM: false,
          requireActivity: false,
          trackEquipment: false,
          trackMaterials: false,
          trackPOBalance: false,
          useCustomRates: false
        }
      });
    } else {
      setEditingCustomer(null);
      setFormData({ 
        name: '', 
        externalId: '', 
        poNumberPrefix: '', 
        jobCode: '',
        paymentTerms: 'NET_30',
        paymentTermsDays: '',
        billingAddress: '',
        billingEmail: '',
        config: {
          useDetailedTM: false,
          requireActivity: false,
          trackEquipment: false,
          trackMaterials: false,
          trackPOBalance: false,
          useCustomRates: false
        }
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Clean up form data - convert empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      externalId: formData.externalId || null,
      poNumberPrefix: formData.poNumberPrefix || null,
      jobCode: formData.jobCode || null,
      paymentTermsDays: formData.paymentTermsDays ? parseInt(formData.paymentTermsDays, 10) : null,
      billingAddress: formData.billingAddress || null,
      billingEmail: formData.billingEmail || null,
      config: formData.config
    };

    try {
      if (editingCustomer) {
        await axios.patch(`${API}/customers/${editingCustomer.id}`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer created successfully');
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to save customer:', error);
      // Handle validation error objects from Pydantic
      let errorMessage = 'Failed to save customer';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors come as array
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || String(e)).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCustomer) return;

    try {
      await axios.delete(`${API}/customers/${deletingCustomer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="customers-page">
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Customers</h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">Manage your customer database</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="create-customer-button"
          className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95 py-4 md:py-2 text-base w-full sm:w-auto"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Customer
        </Button>
      </div>

      {customers.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No customers yet</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="outline"
              className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
            >
              Create your first customer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {customers.map((customer) => (
            customer && customer.id ? (
            <Card
              key={customer.id}
              data-testid={`customer-card-${customer.id}`}
              className="border-slate-200 shadow-sm hover:shadow-md hover:border-[#1A3668] transition-all rounded-sm"
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start justify-between mb-3 md:mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 truncate">{customer.name || 'Unnamed'}</h3>
                    {customer.externalId && (
                      <p className="text-sm text-slate-500 mt-1 font-mono truncate">{customer.externalId}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {customer.jobCode && (
                        <span className="text-xs bg-[#1A3668] text-white px-2 py-1 rounded-sm font-mono">
                          {customer.jobCode}
                        </span>
                      )}
                      {customer.poNumberPrefix && (
                        <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded-sm font-mono">
                          {customer.poNumberPrefix}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {customer.paymentTerms && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-sm flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {PAYMENT_TERMS_OPTIONS.find(o => o.value === customer.paymentTerms)?.label || customer.paymentTerms}
                        </span>
                      )}
                      {customer.billingEmail && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-sm flex items-center gap-1 max-w-full truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{customer.billingEmail}</span>
                        </span>
                      )}
                    </div>
                    {customer.config && Object.values(customer.config).some(v => v === true) && (
                      <div className="flex items-center gap-1 mt-2">
                        <Settings2 className="w-3 h-3 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-sm border border-amber-200">
                          Custom Config
                        </span>
                      </div>
                    )}
                  </div>
                  <Building2 className="w-6 h-6 md:w-8 md:h-8 text-[#1A3668] opacity-50 flex-shrink-0" />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleOpenDialog(customer)}
                    data-testid={`edit-customer-${customer.id}`}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm py-2"
                  >
                    <Pencil className="w-4 h-4 mr-1 md:mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => {
                      setDeletingCustomer(customer);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-customer-${customer.id}`}
                    variant="outline"
                    size="sm"
                    className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            ) : null
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCustomer ? 'Edit Customer' : 'New Customer'}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer ? 'Update customer information' : 'Add a new customer to your database'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Customer Name *
                </Label>
                <Input
                  id="name"
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Construction"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="externalId" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    External ID (Optional)
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[250px]">
                        <p className="text-sm">
                          Use this to link to your accounting system (e.g., QuickBooks Customer ID). 
                          Helps match imported invoices to the correct customer.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="externalId"
                  data-testid="customer-external-id-input"
                  value={formData.externalId}
                  onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                  placeholder="e.g., QB-12345 or ACME-001"
                  className="mt-2 rounded-sm border-2"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Reference ID from your accounting or ERP system
                </p>
              </div>
              <div>
                <Label htmlFor="jobCode" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Job Code *
                </Label>
                <Input
                  id="jobCode"
                  data-testid="customer-job-code-input"
                  value={formData.jobCode}
                  onChange={(e) => setFormData({ ...formData, jobCode: e.target.value.toUpperCase() })}
                  placeholder="BH"
                  maxLength={5}
                  className="mt-2 rounded-sm border-2 font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Used for job numbering (e.g., "BH" → 26BHTM1)
                </p>
              </div>
              <div>
                <Label htmlFor="poNumberPrefix" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Material PO Prefix (Optional)
                </Label>
                <Input
                  id="poNumberPrefix"
                  data-testid="customer-po-prefix-input"
                  value={formData.poNumberPrefix}
                  onChange={(e) => setFormData({ ...formData, poNumberPrefix: e.target.value.toUpperCase() })}
                  placeholder="ACME"
                  maxLength={10}
                  className="mt-2 rounded-sm border-2 font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  If set, Material POs for this customer's jobs will use this prefix (e.g., ACME-0001)
                </p>
              </div>
              
              {/* Billing Settings Section */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-4">Billing Settings</h4>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="paymentTerms" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      Payment Terms
                    </Label>
                    <Select
                      value={formData.paymentTerms}
                      onValueChange={(value) => setFormData({ ...formData, paymentTerms: value })}
                    >
                      <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="customer-payment-terms-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_TERMS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.paymentTerms === 'CUSTOM' && (
                    <div>
                      <Label htmlFor="paymentTermsDays" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                        Custom Days
                      </Label>
                      <Input
                        id="paymentTermsDays"
                        type="number"
                        data-testid="customer-payment-days-input"
                        value={formData.paymentTermsDays}
                        onChange={(e) => setFormData({ ...formData, paymentTermsDays: e.target.value })}
                        placeholder="45"
                        min="1"
                        className="mt-2 rounded-sm border-2"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="billingEmail" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      Billing Email
                    </Label>
                    <Input
                      id="billingEmail"
                      type="email"
                      data-testid="customer-billing-email-input"
                      value={formData.billingEmail}
                      onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                      placeholder="billing@customer.com"
                      className="mt-2 rounded-sm border-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="billingAddress" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      Billing Address
                    </Label>
                    <Textarea
                      id="billingAddress"
                      data-testid="customer-billing-address-input"
                      value={formData.billingAddress}
                      onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                      placeholder="123 Main St&#10;City, State 12345"
                      rows={3}
                      className="mt-2 rounded-sm border-2"
                    />
                  </div>
                </div>
              </div>

              {/* Client Configuration Section */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-1">Client Configuration</h4>
                <p className="text-xs text-slate-500 mb-4">Enable custom T&M features for this client's jobs and timesheets</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Detailed T&M Format</p>
                      <p className="text-xs text-slate-500">Use detailed Time & Material timesheet layout</p>
                    </div>
                    <Switch
                      data-testid="config-detailed-tm"
                      checked={formData.config?.useDetailedTM || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, useDetailedTM: v } })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Activity Breakdown</p>
                      <p className="text-xs text-slate-500">Require activity type on labor entries</p>
                    </div>
                    <Switch
                      data-testid="config-require-activity"
                      checked={formData.config?.requireActivity || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, requireActivity: v } })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Equipment Tracking</p>
                      <p className="text-xs text-slate-500">Track equipment used per entry</p>
                    </div>
                    <Switch
                      data-testid="config-track-equipment"
                      checked={formData.config?.trackEquipment || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, trackEquipment: v } })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Material Tracking</p>
                      <p className="text-xs text-slate-500">Track materials used per entry</p>
                    </div>
                    <Switch
                      data-testid="config-track-materials"
                      checked={formData.config?.trackMaterials || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, trackMaterials: v } })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">PO Balance Tracking</p>
                      <p className="text-xs text-slate-500">Show PO balance on timesheets (cross-job)</p>
                    </div>
                    <Switch
                      data-testid="config-track-po-balance"
                      checked={formData.config?.trackPOBalance || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, trackPOBalance: v } })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Custom Rates</p>
                      <p className="text-xs text-slate-500">Use client-specific rate tables instead of standard</p>
                    </div>
                    <Switch
                      data-testid="config-custom-rates"
                      checked={formData.config?.useCustomRates || false}
                      onCheckedChange={(v) => setFormData({ ...formData, config: { ...formData.config, useCustomRates: v } })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-customer-button"
                disabled={submitting}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {submitting ? 'Saving...' : editingCustomer ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingCustomer?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-customer"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
